import React, { useCallback } from "react";
import { classNames } from "../../common_util";
import { Product } from "../types/catalog";
import { buildTagElementsForTags } from "./product_tags";
import { ReactComponent as Delete } from "images/icons/delete.svg";
import { ReactComponent as TagFavorite } from "images/icons/tags-favorite.svg";
import { useCart, useModal, useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { NavigationState, serializeShopState, StorefrontPage } from "../types/navigation";

export const ProductListing = ({ product }: { product: Product }): React.JSX.Element => {
  const showModal = useModal();
  const cart = useCart();

  const {
    presentationStatus, priceFormatter, hideOutOfStockItems
  } = useStorefrontConfig();

  const {
    isOrderable, hasImage, isInFulfillableTimeRange, dealDescriptionForProduct,
    getPrimaryImageUrl
  } = useStorefrontHelper();

  const hideOutOfStockProductListing = !isOrderable(product) && hideOutOfStockItems;
  const priceDisplay = product.price ? priceFormatter.format(product.price / 100.0) : "";
  const countInCart = cart?.getCountOfItemInCart(product) || 0;
  const isAvailable = presentationStatus == "open_for_business" && isInFulfillableTimeRange(product) && isOrderable(product);
  const outOfStockBadgeHTML = <>
    <div className="badge is-strong is-hidden-above-desktop">Out of stock</div>
    <div className="badge is-strong is-large is-visible-above-desktop"><Delete className="icon streamline-icon" /><span>Out of stock</span></div>
  </>
  const unavailableProductComponent = isOrderable(product) ? undefined : outOfStockBadgeHTML

  const productModalOnClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    showModal(product, true);
  }, [product, showModal])

  if (hideOutOfStockProductListing) return <></>;

  const classes = classNames({
    "product-listing": !hasImage(product),
    "product-listing-with-image": !!hasImage(product),
    "is-available": isAvailable,
    "is-unavailable": !isAvailable,
  });

  const [primaryTags, supplementaryTags] = buildTagElementsForTags(product.tags);

  // Assume only one deal per product for now
  const deal = dealDescriptionForProduct(product);
  const dealTag = deal ? <div className='deals'>
    <div className={`badge is-important`} key={`badge-deal`}>
      <TagFavorite className="icon streamline-icon"/>
      <span>{deal}</span>
    </div>
  </div> : <></>

  const descriptionBlock = (() => {
    return (
      <div className="description">
        <p>{product.description}</p>
        {supplementaryTags}
      </div>
    );
  })();

  const productDetailsInner = <>
    <div className="title">{product.title}</div>
    <div className="price"><span>{priceDisplay}</span>{unavailableProductComponent}</div>
    {countInCart > 0 && !hasImage(product) ?
      <div className="badge is-quantity" data-testid="quantity">{countInCart}</div>
    : <></>}

    {descriptionBlock}

    {primaryTags}
    {dealTag}
  </>

  const productDetails = hasImage(product) ? <div className="product-details">{productDetailsInner}</div> : productDetailsInner;
  const productImage = !!hasImage(product) && <div
    className="product-image"
    data-lazy-images-target="image"
    data-lazy-images-imagery-path-value={getPrimaryImageUrl(product)}
  >
    {countInCart > 0 && hasImage(product) ?
      <div className="badge is-quantity is-round" data-testid="quantity">{countInCart}</div>
    : <></>}
  </div>;

  const fakeLinkHref = `#${serializeShopState(new NavigationState(StorefrontPage.Shop, "", [], product.fullKeyPath()))}`;

  return (
    <a href={fakeLinkHref} className={classes} onClick={productModalOnClick} role="link" data-cy={isAvailable && "menu-item"} data-testid="product-listing">
      {productDetails}
      {productImage}
    </a>
  );
}
